import { Inject, Injectable, inject } from '@angular/core';
import * as ExtensionAppActions from '../state/extension-app.actions';
import { Store } from '@ngrx/store';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { APP_BASE_HREF } from '@angular/common';
import { distinctUntilChanged, map, Observable, shareReplay, startWith } from 'rxjs';
import { IntegratedAppComponentType } from '@pcp/core/interfaces';
import { ClassroomFacade } from 'shared/classroom/data-access';

@Injectable({
    providedIn: 'root',
})
export class ExtensionAppService {
    private readonly lessonYjs = inject(ClassroomFacade);

    /**
     * The currently opened integrated app or null. Sourced from Y.Doc via facade.
     *
     * Shared so the three boolean derivatives below (and any template subscribers) share
     * one upstream subscription to `state$` instead of each re-running the chain.
     */
    readonly integratedApp$: Observable<IntegratedAppComponentType> = this.lessonYjs.state$.pipe(
        map(s => s.activeExtensionApp ?? null),
        startWith(null as IntegratedAppComponentType),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /** Emits true/false when any integrated app is shown/hidden. */
    readonly showIntegratedApp$: Observable<boolean> = this.integratedApp$.pipe(map(app => !!app));

    /** Emits true/false when PushTheButton is shown/hidden. */
    readonly showPushTheButton$: Observable<boolean> = this.integratedApp$.pipe(map(app => app === 'PushTheButton'));

    /** Emits true/false when PracticeTrainer is shown/hidden. */
    readonly showPracticeTrainer$: Observable<boolean> = this.integratedApp$.pipe(map(app => app === 'PracticeTrainer'));

    constructor(
        private readonly store: Store,
        private readonly sanitizer: DomSanitizer,
        @Inject(APP_BASE_HREF) private readonly baseHref: string,
    ) {}

    getPushTheButtonStylesheetUrl(): SafeResourceUrl {
        /**
          * NOTE: in order to work, the stylesheet must be compiled using each apps' projects.json like this:
         *
         * ```
         *  "styles": [
         *                     "apps/teacher/src/styles.scss",
         *                     {
         *                         "input": "libs/extension-apps/push-the-button/styles/styles.scss",
         *                         "inject": false,
         *                         "bundleName": "push-the-button"
         *                     }
         *                 ],
         * ```
          */
        return this.sanitizer.bypassSecurityTrustResourceUrl(`${this.baseHref}push-the-button.css`);
    }

    getPracticeTrainerStylesheetUrl(): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl(`${this.baseHref}practice-trainer.css`);
    }

    openPushTheButton() {
        this.store.dispatch(ExtensionAppActions.requestOpenExtensionApp({ extensionApp: 'PushTheButton' }));
    }

    openPracticeTrainer() {
        this.store.dispatch(ExtensionAppActions.requestOpenExtensionApp({ extensionApp: 'PracticeTrainer' }));
    }

    /**
     * Closes any extension app which is currently opened.
     */
    closeExtensionApp() {
        this.store.dispatch(ExtensionAppActions.closeExtensionApp());
    }
}
